import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function EmployerPage() {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const pricingPlans = [
    {
      id: 'startup',
      name: 'Startup',
      price: '$299',
      period: 'per month',
      description: 'Perfect for growing companies',
      features: [
        '5 active job postings',
        'Featured listing for 7 days',
        'Basic analytics dashboard',
        'Email support',
        'Candidate filtering',
        'Mobile-optimized listings'
      ],
      icon: 'ri-rocket-line',
      popular: false
    },
    {
      id: 'scaleup',
      name: 'Scale-up',
      price: '$599',
      period: 'per month',
      description: 'For rapidly expanding teams',
      features: [
        '15 active job postings',
        'Featured listing for 14 days',
        'Advanced analytics & insights',
        'Priority email & chat support',
        'AI-powered candidate matching',
        'Custom branding options',
        'Dedicated account manager',
        'API access'
      ],
      icon: 'ri-line-chart-line',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      period: 'contact us',
      description: 'Tailored for large organizations',
      features: [
        'Unlimited job postings',
        'Always featured listings',
        'Enterprise analytics suite',
        '24/7 premium support',
        'Advanced AI recruitment tools',
        'Full white-label solution',
        'Multiple team accounts',
        'Custom integrations',
        'SLA guarantee'
      ],
      icon: 'ri-building-line',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />
      
      <div className="pt-20">
        {/* Hero Section */}
        <div className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/99134e32722b9994215843767b8d77e2.jpg"
              alt="Employer hero"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/90 to-[#0a0a0a]/70"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 py-20">
            <div className="max-w-3xl">
              <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-[#00f0ff] to-[#a855f7] bg-clip-text text-transparent">
                Hire Top Talent Faster
              </h1>
              <p className="text-2xl text-gray-300 mb-8 leading-relaxed">
                Connect with qualified candidates instantly. Post jobs, review applications, and build your dream team with Zing's powerful recruitment platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#pricing"
                  className="inline-block bg-gradient-to-r from-[#00f0ff] to-[#a855f7] px-10 py-5 rounded-xl font-semibold text-lg hover:scale-105 transition-transform whitespace-nowrap cursor-pointer text-center"
                >
                  View Pricing
                </a>
                <button className="px-10 py-5 bg-[#1a1a1a] border-2 border-[#00f0ff] rounded-xl font-semibold text-lg hover:scale-105 transition-transform whitespace-nowrap cursor-pointer">
                  Schedule Demo
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-r from-[#00f0ff]/10 to-[#a855f7]/10 border-y border-white/10 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { number: '10K+', label: 'Active Companies', icon: 'ri-building-line' },
                { number: '1M+', label: 'Qualified Candidates', icon: 'ri-team-line' },
                { number: '50K+', label: 'Successful Hires', icon: 'ri-user-star-line' },
                { number: '4.9/5', label: 'Customer Rating', icon: 'ri-star-fill' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <i className={`${stat.icon} text-5xl text-[#00f0ff] mb-4 block`}></i>
                  <div className="text-4xl font-bold mb-2">{stat.number}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-[#00f0ff] to-[#a855f7] bg-clip-text text-transparent">
                Everything You Need to Hire
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Powerful tools and features designed to streamline your recruitment process
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-search-eye-line',
                  title: 'Smart Candidate Matching',
                  description: 'AI-powered algorithms match your job postings with the most qualified candidates automatically'
                },
                {
                  icon: 'ri-dashboard-line',
                  title: 'Analytics Dashboard',
                  description: 'Track application metrics, view engagement stats, and optimize your job postings in real-time'
                },
                {
                  icon: 'ri-star-line',
                  title: 'Featured Listings',
                  description: 'Boost visibility with featured job postings that appear at the top of search results'
                },
                {
                  icon: 'ri-filter-3-line',
                  title: 'Advanced Filtering',
                  description: 'Filter candidates by skills, experience, location, and custom criteria to find perfect matches'
                },
                {
                  icon: 'ri-smartphone-line',
                  title: 'Mobile Optimized',
                  description: 'Manage applications and communicate with candidates on the go with our mobile-friendly platform'
                },
                {
                  icon: 'ri-team-line',
                  title: 'Team Collaboration',
                  description: 'Collaborate with your hiring team, share notes, and make decisions together seamlessly'
                }
              ].map((feature, index) => (
                <div
                  key={index}
                  className="bg-[#1a1a1a]/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-[#00f0ff]/50 transition-all hover:scale-105 cursor-pointer"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00f0ff]/20 to-[#a855f7]/20 rounded-xl mb-6">
                    <i className={`${feature.icon} text-3xl text-[#00f0ff]`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div id="pricing" className="py-24 px-4 bg-gradient-to-b from-transparent to-[#00f0ff]/5">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-[#00f0ff] to-[#a855f7] bg-clip-text text-transparent">
                Choose Your Plan
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Flexible pricing options to match your hiring needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {pricingPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative bg-[#1a1a1a]/50 backdrop-blur-xl border rounded-3xl p-8 transition-all hover:scale-105 cursor-pointer ${
                    plan.popular
                      ? 'border-[#00f0ff] shadow-2xl shadow-[#00f0ff]/20'
                      : 'border-white/10 hover:border-[#00f0ff]/50'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-[#00f0ff] to-[#a855f7] px-6 py-2 rounded-full text-sm font-bold">
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#00f0ff]/20 to-[#a855f7]/20 rounded-xl mb-4">
                      <i className={`${plan.icon} text-3xl text-[#00f0ff]`}></i>
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    <p className="text-gray-400 mb-6">{plan.description}</p>
                    <div className="mb-2">
                      <span className="text-5xl font-bold">{plan.price}</span>
                    </div>
                    <div className="text-gray-400">{plan.period}</div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-[#00f0ff] text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full py-4 rounded-xl font-semibold text-lg transition-all whitespace-nowrap cursor-pointer ${
                      plan.popular
                        ? 'bg-gradient-to-r from-[#00f0ff] to-[#a855f7] hover:scale-105'
                        : 'bg-[#1a1a1a] border-2 border-[#00f0ff] hover:bg-[#00f0ff]/10'
                    }`}
                  >
                    {plan.id === 'enterprise' ? 'Contact Sales' : 'Select Plan'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-24 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#00f0ff] to-[#a855f7] rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition duration-300"></div>
              <div className="relative bg-[#1a1a1a] rounded-3xl p-16 border border-white/10 text-center">
                <h2 className="text-5xl font-bold mb-6">Ready to Start Hiring?</h2>
                <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
                  Join thousands of companies finding their perfect candidates on Zing
                </p>
                <button className="bg-gradient-to-r from-[#00f0ff] to-[#a855f7] px-12 py-5 rounded-xl font-semibold text-lg hover:scale-105 transition-transform whitespace-nowrap cursor-pointer">
                  Post Your First Job
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
